import type { AnswerValue, Entry, MedicationIntake, Question, Scenario } from "../entities";
import { sameValue, ScenarioRunner } from "../scenario/ScenarioRunner";
import { isTimePeriod, periodOfTime, TIME_PERIOD_OPTIONS } from "../scenario/timePeriods";
import { periodLength, type Period } from "./periods";

export interface OptionCount {
  /** An option id from the scenario, or "other" for free-text entries. */
  optionId: string;
  count: number;
}

export interface MedicationCount {
  /** First spelling seen; matching is case-insensitive. */
  name: string;
  intakes: number;
  helped: number;
}

export type QuestionReport =
  | { questionId: string; type: "yesno"; answered: number; yes: number }
  | { questionId: string; type: "scale" | "number"; answered: number; avg: number; min: number; max: number }
  | { questionId: string; type: "single" | "multi"; answered: number; options: OptionCount[] }
  | { questionId: string; type: "medication"; answered: number; total: number; byName: MedicationCount[] }
  | { questionId: string; type: "text"; answered: number }
  | { questionId: string; type: "time"; answered: number; /** Present when the question accepts periods: counts per time of day. */ options?: OptionCount[] };

export interface Report {
  period: Period;
  /** Length of the period in days. */
  days: number;
  /** Distinct days with at least one non-skipped entry. */
  recorded: number;
  /** Distinct days where every entry was skipped. */
  skipped: number;
  /** Non-skipped entries; equals `recorded` for daily scenarios. */
  entries: number;
  /** Entries whose gate answer matches the scenario's episode gate; null for daily scenarios. */
  episodes: number | null;
  questions: QuestionReport[];
}

export function isEmptyAnswer(question: Question, value: AnswerValue): boolean {
  switch (question.type) {
    case "multi":
    case "medication":
      return Array.isArray(value) && value.length === 0;
    case "scale":
    case "number":
      return typeof value !== "number" || Number.isNaN(value);
    case "yesno":
      return typeof value !== "boolean";
    default:
      return value === "";
  }
}

export class ReportBuilder {
  build(scenario: Scenario, entries: Entry[], period: Period): Report {
    const runner = new ScenarioRunner(scenario);
    const inPeriod = entries.filter((e) => !e.deleted && e.date >= period.from && e.date <= period.to);
    const recorded = inPeriod.filter((e) => e.status !== "skipped");
    const recordedDays = new Set(recorded.map((e) => e.date));
    const skippedDays = new Set(inPeriod.filter((e) => !recordedDays.has(e.date)).map((e) => e.date));
    const gate = scenario.episodes;
    const episodes = gate
      ? recorded.filter((e) => {
          const value = e.answers[gate.gateQuestionId];
          return value !== undefined && sameValue(value, gate.gateValue);
        }).length
      : null;
    return {
      period,
      days: periodLength(period),
      recorded: recordedDays.size,
      skipped: skippedDays.size,
      entries: recorded.length,
      episodes,
      questions: scenario.questions.map((q) => this.forQuestion(q, recorded, runner)),
    };
  }

  private values(question: Question, entries: Entry[], runner: ScenarioRunner): AnswerValue[] {
    const out: AnswerValue[] = [];
    for (const entry of entries) {
      if (!runner.visibleQuestions(entry.answers).some((q) => q.id === question.id)) continue;
      const value = entry.answers[question.id];
      if (value === undefined || isEmptyAnswer(question, value)) continue;
      out.push(value);
    }
    return out;
  }

  private forQuestion(question: Question, entries: Entry[], runner: ScenarioRunner): QuestionReport {
    const values = this.values(question, entries, runner);
    const questionId = question.id;
    switch (question.type) {
      case "yesno":
        return { questionId, type: "yesno", answered: values.length, yes: values.filter((v) => v === true).length };
      case "scale":
      case "number": {
        const numbers = values.filter((v): v is number => typeof v === "number");
        const sum = numbers.reduce((a, b) => a + b, 0);
        return {
          questionId, type: question.type, answered: numbers.length,
          avg: numbers.length ? sum / numbers.length : 0,
          min: numbers.length ? Math.min(...numbers) : 0,
          max: numbers.length ? Math.max(...numbers) : 0,
        };
      }
      case "single":
      case "multi": {
        const counts = new Map<string, number>(question.options.map((o) => [o.id, 0]));
        if (question.type === "multi" && question.allowOther) counts.set("other", 0);
        for (const value of values) {
          const picked = Array.isArray(value) ? (value as string[]) : [value as string];
          for (const id of picked) {
            const key = id.startsWith("other:") ? "other" : id;
            counts.set(key, (counts.get(key) ?? 0) + 1);
          }
        }
        const order = [...counts.keys()];
        const options = order
          .map((optionId) => ({ optionId, count: counts.get(optionId) ?? 0 }))
          .sort((a, b) => b.count - a.count || order.indexOf(a.optionId) - order.indexOf(b.optionId));
        return { questionId, type: question.type, answered: values.length, options };
      }
      case "medication": {
        const byKey = new Map<string, MedicationCount>();
        let total = 0;
        for (const value of values) {
          for (const intake of value as MedicationIntake[]) {
            const key = intake.name.trim().toLowerCase();
            const row = byKey.get(key) ?? { name: intake.name.trim(), intakes: 0, helped: 0 };
            row.intakes += intake.count;
            if (intake.helped === "yes") row.helped += 1;
            byKey.set(key, row);
            total += intake.count;
          }
        }
        const byName = [...byKey.values()].sort((a, b) => b.intakes - a.intakes || a.name.localeCompare(b.name));
        return { questionId, type: "medication", answered: values.length, total, byName };
      }
      case "time": {
        if (!question.periods) return { questionId, type: "time", answered: values.length };
        const counts = new Map<string, number>(TIME_PERIOD_OPTIONS.map((o) => [o.id, 0]));
        for (const value of values) {
          const period = isTimePeriod(value as string) ? (value as string) : periodOfTime(value as string);
          counts.set(period, (counts.get(period) ?? 0) + 1);
        }
        const order = [...counts.keys()];
        const options = order
          .map((optionId) => ({ optionId, count: counts.get(optionId) ?? 0 }))
          .sort((a, b) => b.count - a.count || order.indexOf(a.optionId) - order.indexOf(b.optionId));
        return { questionId, type: "time", answered: values.length, options };
      }
      case "text":
        return { questionId, type: "text", answered: values.length };
    }
  }
}
